import {
  removeBackgroundSingle,
  getJobStatus,
  fetchCreationImageBlob,
  fetchUploadImageBlob,
} from "../services/api/editor/background_removal";
import { resolveCreationImageUrl } from "./resolveCreationImageUrl";
import { urlToFile } from "./urlToFile";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function pollJobUntilDone(jobId, token, { intervalMs = 2000, timeoutMs = 120000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const status = await getJobStatus(token, jobId);
    if (status?.status === "finished") {
      if (status.result?.success && status.result?.output_url) {
        return {
          outputUrl: status.result.output_url,
          creationId: status.result.creation_id ?? null,
          s3Url: status.result.s3_url,
        };
      }
      throw new Error(status.result?.error || status.error || "Background removal failed");
    }
    if (status?.status === "failed") {
      throw new Error(status.error || "Background removal failed");
    }
    await sleep(intervalMs);
  }
  throw new Error("Background removal timed out");
}

function isRemoteHttp(url) {
  return (
    typeof url === "string" &&
    /^https?:\/\//i.test(url) &&
    !url.startsWith("blob:") &&
    !url.startsWith("data:")
  );
}

function isLocalUrl(url) {
  return (
    typeof url === "string" &&
    (url.startsWith("blob:") || url.startsWith("data:"))
  );
}

/**
 * Build a File for BG removal without browser→S3/MinIO CORS.
 * Prefer local File / blob URL, then authenticated upload/creation APIs.
 * Never fetch remote CDN URLs from the browser.
 */
async function resolveInputFile({
  token,
  imageFile,
  imageUrl,
  rootCreationId,
  creationId,
}) {
  if (imageFile) return imageFile;

  if (isLocalUrl(imageUrl)) {
    return urlToFile(imageUrl, "bg-remove-input.png");
  }

  const rootId =
    rootCreationId != null && !Number.isNaN(Number(rootCreationId))
      ? Number(rootCreationId)
      : null;
  const cid =
    creationId != null && !Number.isNaN(Number(creationId))
      ? Number(creationId)
      : null;

  // Uploads library originals — same-origin API (no S3 CORS).
  if (token && rootId && rootId > 0) {
    const blob = await fetchUploadImageBlob(token, rootId);
    if (blob && !(blob.type && String(blob.type).includes("json"))) {
      return new File([blob], "bg-remove-input.png", {
        type: blob.type || "image/png",
      });
    }
    throw new Error("Could not load upload image from API for background removal.");
  }

  // Existing creation output as input — also same-origin.
  if (token && cid && cid > 0) {
    const blob = await fetchCreationImageBlob(token, cid);
    if (blob && !(blob.type && String(blob.type).includes("json"))) {
      return new File([blob], "bg-remove-input.png", {
        type: blob.type || "image/png",
      });
    }
    throw new Error("Could not load creation image from API for background removal.");
  }

  if (isRemoteHttp(imageUrl)) {
    throw new Error(
      "Cannot load this image for background removal (CORS). Re-place it from Uploads and try again."
    );
  }

  if (imageUrl) {
    return urlToFile(imageUrl, "bg-remove-input.png");
  }
  return null;
}

/**
 * Run BG removal (or reuse cache) and return a canvas-safe blob:/data: URL.
 * Prefer creation image API over S3 to avoid CORS tainting in CanvasEditor.
 *
 * @param {Object} opts
 * @param {string} [opts.quality="high"] - BFF quality: low | medium | standard | high
 *   (PhotoStudio maps high/fine/best → fine edges, else standard)
 */
export async function ensureBackgroundRemoved({
  token,
  imageFile = null,
  imageUrl = null,
  cachedCutoutUrl = null,
  rootCreationId = null,
  creationId = null,
  lineage = null,
  quality = "high",
} = {}) {
  if (
    cachedCutoutUrl &&
    (cachedCutoutUrl.startsWith("blob:") || cachedCutoutUrl.startsWith("data:"))
  ) {
    return { cutoutUrl: cachedCutoutUrl, creationId: null };
  }

  if (!token) {
    throw new Error("Authentication required");
  }

  const file = await resolveInputFile({
    token,
    imageFile,
    imageUrl,
    rootCreationId,
    creationId,
  });
  if (!file) {
    throw new Error("No image available for background removal");
  }

  // Prefer explicit lineage; otherwise attach to upload root when known (overlay path).
  let requestLineage = lineage;
  if (
    !requestLineage &&
    rootCreationId != null &&
    !Number.isNaN(Number(rootCreationId)) &&
    Number(rootCreationId) > 0
  ) {
    const rootId = Number(rootCreationId);
    requestLineage = {
      root_creation_id: rootId,
      parent_creation_id: rootId,
    };
  }

  const edgeQuality =
    typeof quality === "string" && quality.trim() ? quality.trim() : "high";

  const response = await removeBackgroundSingle(
    token,
    file,
    edgeQuality,
    "png",
    requestLineage
  );
  let outputUrl = response.output_url;
  let outCreationId = response.creation_id ?? null;
  let s3Url = response.s3_url;

  if (!outputUrl && response.job_id) {
    const polled = await pollJobUntilDone(response.job_id, token);
    outputUrl = polled.outputUrl;
    outCreationId = polled.creationId;
    s3Url = polled.s3Url;
  }

  if (!outputUrl && !s3Url) {
    throw new Error("Background removal returned no image");
  }

  const remoteUrl = outputUrl || s3Url;
  const resolved = await resolveCreationImageUrl(token, remoteUrl, outCreationId);
  let cutoutUrl = resolved.displayUrl;
  const resolvedCreationId = resolved.creationId ?? outCreationId;

  // CanvasEditor needs blob:/data: — never browser-fetch S3/MinIO (CORS on local + AWS).
  if (isRemoteHttp(cutoutUrl)) {
    if (!resolvedCreationId) {
      throw new Error(
        "Background removal succeeded but no creation id was returned to load the cutout."
      );
    }
    const blob = await fetchCreationImageBlob(token, Number(resolvedCreationId));
    if (!blob || (blob.type && String(blob.type).includes("json"))) {
      throw new Error("Could not load background-removed image from API.");
    }
    cutoutUrl = URL.createObjectURL(blob);
  }

  if (!cutoutUrl || isRemoteHttp(cutoutUrl)) {
    throw new Error(
      "Background-removed image could not be loaded for the editor. Please try again."
    );
  }

  return {
    cutoutUrl,
    creationId: resolvedCreationId,
  };
}
